import { DAILY_STATISTICS_OPTIONS, statisticsSession } from '../../api/statisticsQueries';
import PageTabs from '../../ui/PageTabs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CircleAlert,
  CircleCheck,
  ExternalLink,
  Pin,
  RefreshCw,
  Search,
  Star,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import {  Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGaBreakdown, getGaDashboard } from '../../api/gaAdmin';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { t } from '../../utils/i18n';
import {
  fillGaTimeseries,
  formatPercent,
  GA_PRESET_30D,
  GA_RANGE_30D,
  getGaDateRange,
  getLatestGaRow,
} from '../../utils/gaDashboard';
import {
  buildEventComparisonRows,
  crashHealthTone,
  filterAndSortEvents,
  prioritizeFavoriteEvents,
  previousGaDateRange,
  readFavoriteEvents,
  summarizeEventAnomalies,
  toEventTrendRows,
  toggleFavoriteEvent,
  writeFavoriteEvents,
} from '../../utils/analyticsDashboard';
import { getFirebaseCrashlyticsUrl } from '../../utils/firebaseCrashlytics';
import DashboardKpi from '../Dashboard/components/DashboardKpi';
import DashboardPanel from '../Dashboard/components/DashboardPanel';
import GaBreakdownCard from '../Dashboard/ga/GaBreakdownCard';
import GaDateRangeControls from '../Dashboard/ga/GaDateRangeControls';
import GaKpiCards from '../Dashboard/ga/GaKpiCards';
import GaMetricLineChart from '../Dashboard/ga/GaMetricLineChart';
import GaUserActivityChart from '../Dashboard/ga/GaUserActivityChart';
import GrowthTab from './GrowthTab';

const TAB_KEYS = ['overview', 'growth', 'events', 'crash'];

function Select({ label, value, onChange, options, optionLabels = {}, includeAll = true }) {
  return (
    <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-dark-text-muted">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-dark-border bg-dark-surface px-3 py-2 text-sm text-dark-text outline-none focus:border-primary"
      >
        {includeAll && <option value="">{t('analytics.events.all')}</option>}
        {(options || []).map((option) => (
          <option key={option} value={option}>
            {optionLabels[option] || option}
          </option>
        ))}
      </select>
    </label>
  );
}

function OverviewTab({ rows, isLoading, from, to, token, apiEnvironment }) {
  const dimensions = ['country', 'platform', 'appVersion', 'device'];
  const queries = useQueries({
    queries: dimensions.map((dimension) => ({
      ...DAILY_STATISTICS_OPTIONS,
      queryKey: ['gaBreakdown', apiEnvironment, dimension, from, to, statisticsSession(token)],
      queryFn: () => getGaBreakdown(dimension, from, to, token),
      enabled: !!token,
    })),
  });
  const chartData = useMemo(() => fillGaTimeseries(rows, from, to), [rows, from, to]);

  const hasBreakdownError = queries.some((query) => query.error);

  return (
    <div className="space-y-4">
      {hasBreakdownError && (
        <p className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
          {t('dashboard.ga.loadError')}
        </p>
      )}
      <GaKpiCards rows={rows} isLoading={isLoading} />
      <GaUserActivityChart data={chartData} isLoading={isLoading} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {queries.map((query, index) =>
          query.isLoading || query.data?.some((item) => item.activeUsers > 0) ? (
            <GaBreakdownCard
              key={dimensions[index]}
              title={t(`dashboard.ga.breakdown.${dimensions[index]}`)}
              items={query.data}
              isLoading={query.isLoading}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

function EventAnomalyRow({ row, pinned = false }) {
  const isIncrease = row.anomalyType === 'increase';
  const isCritical = row.severity === 'critical';
  const Icon = isCritical ? CircleAlert : isIncrease ? TrendingUp : TrendingDown;
  const toneClass = isCritical
    ? 'border-red-900/60 bg-red-950/30 text-red-300'
    : isIncrease
      ? 'border-sky-900/60 bg-sky-950/30 text-sky-300'
      : 'border-amber-900/60 bg-amber-950/30 text-amber-300';

  return (
    <li className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 ${toneClass}`}>
      <div className="flex min-w-0 items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 break-all text-sm font-semibold">
            {pinned && <Pin className="h-3.5 w-3.5 shrink-0" />}
            {row.dimension}
          </p>
          <p className="mt-0.5 text-xs opacity-80">
            {t(`analytics.events.anomalies.${row.anomalyType}`)}
          </p>
        </div>
      </div>
      <div className="shrink-0 text-right text-xs tabular-nums">
        <p className="font-semibold">
          {row.changeRate == null ? '—' : formatPercent(row.changeRate, 0)}
        </p>
        <p className="mt-0.5 opacity-80">
          {row.previousEventCount.toLocaleString()} → {row.eventCount.toLocaleString()}
        </p>
      </div>
    </li>
  );
}

function EventAnomalySummary({ rows, favoriteEvents, isLoading, hasError }) {
  const summary = useMemo(
    () => summarizeEventAnomalies(rows, favoriteEvents),
    [favoriteEvents, rows],
  );
  const anomalyCount = summary.pinnedAlerts.length + summary.movers.length;

  return (
    <DashboardPanel
      title={t('analytics.events.anomalies.title')}
      subtitle={t('analytics.events.anomalies.description')}
    >
      {isLoading ? (
        <p className="py-8 text-center text-dark-text-secondary">{t('common.loading')}</p>
      ) : hasError ? (
        <p className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
          {t('analytics.events.anomalies.loadError')}
        </p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-dark-text-secondary">{t('dashboard.ga.noData')}</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-dark-border bg-dark-surface px-3 py-2.5">
              <p className="text-xs text-dark-text-secondary">{t('analytics.events.anomalies.monitored')}</p>
              <p className="mt-1 text-xl font-semibold text-dark-text">{summary.monitoredCount}</p>
            </div>
            <div className="rounded-lg border border-dark-border bg-dark-surface px-3 py-2.5">
              <p className="text-xs text-dark-text-secondary">{t('analytics.events.anomalies.needsAttention')}</p>
              <p className={`mt-1 text-xl font-semibold ${summary.pinnedAlerts.length ? 'text-red-300' : 'text-emerald-300'}`}>
                {summary.pinnedAlerts.length}
              </p>
            </div>
            <div className="rounded-lg border border-dark-border bg-dark-surface px-3 py-2.5">
              <p className="text-xs text-dark-text-secondary">{t('analytics.events.anomalies.significantChanges')}</p>
              <p className="mt-1 text-xl font-semibold text-dark-text">{summary.movers.length}</p>
            </div>
          </div>

          {summary.monitoredCount === 0 && (
            <p className="rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm text-dark-text-secondary">
              {t('analytics.events.anomalies.pinHint')}
            </p>
          )}

          {anomalyCount === 0 ? (
            <p className="flex items-center gap-2 rounded-lg border border-emerald-900/50 bg-emerald-950/20 px-3 py-2.5 text-sm text-emerald-300">
              <CircleCheck className="h-4 w-4" />
              {t('analytics.events.anomalies.healthy')}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-dark-text-secondary">
                  {t('analytics.events.anomalies.pinnedTitle')}
                </h3>
                {summary.pinnedAlerts.length ? (
                  <ul className="space-y-2">
                    {summary.pinnedAlerts.map((row) => (
                      <EventAnomalyRow key={row.dimension} row={row} pinned />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-dark-text-secondary">{t('analytics.events.anomalies.noPinnedIssues')}</p>
                )}
              </div>
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-dark-text-secondary">
                  {t('analytics.events.anomalies.moversTitle')}
                </h3>
                {summary.movers.length ? (
                  <ul className="space-y-2">
                    {summary.movers.map((row) => (
                      <EventAnomalyRow key={row.dimension} row={row} />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-dark-text-secondary">{t('analytics.events.anomalies.noMovers')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardPanel>
  );
}

function EventsTab({ rows, isLoading, from, to, token, apiEnvironment }) {
  const location = useLocation();
  const navigate = useNavigate();
  const urlFilters = new URLSearchParams(location.search);
  const rawPlatform = urlFilters.get('platform') || '';
  const urlPlatform = ({ ios: 'iOS', android: 'Android', all: '' })[rawPlatform.toLowerCase()] ?? rawPlatform;
  const urlVersion = (urlFilters.get('version') || urlFilters.get('appVersion') || '').slice(0, 32);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('eventCount');
  const [platform, setPlatform] = useState(urlPlatform);
  const [appVersion, setAppVersion] = useState(urlVersion);
  useEffect(() => { setPlatform(urlPlatform); setAppVersion(urlVersion); }, [urlPlatform, urlVersion]);
  const updateFilter = (key, value) => {
    if (key === 'platform') setPlatform(value); else setAppVersion(value);
    const params = new URLSearchParams(location.search);
    if (value) params.set(key, value); else params.delete(key);
    if (key === 'version') params.delete('appVersion');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' });
  };
  const selectedEvent = (urlFilters.get('event') || '').slice(0, 128);
  const setSelectedEvent = value => {
    const params = new URLSearchParams(location.search);
    if (value) params.set('event', value); else params.delete('event');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
  };
  const [favoriteEvents, setFavoriteEvents] = useState(() =>
    readFavoriteEvents(typeof window === 'undefined' ? null : window.localStorage),
  );
  const previousRange = useMemo(() => previousGaDateRange(from, to), [from, to]);
  const filters = useMemo(() => ({ platform, appVersion }), [platform, appVersion]);
  const currentQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: ['gaBreakdown', apiEnvironment, 'eventName', from, to, filters, statisticsSession(token)],
    queryFn: () => getGaBreakdown('eventName', from, to, token, filters),
    enabled: !!token,
  });
  const previousQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: [
      'gaBreakdown',
      apiEnvironment,
      'eventName',
      previousRange.from,
      previousRange.to,
      filters,
      statisticsSession(token),
    ],
    queryFn: () =>
      getGaBreakdown('eventName', previousRange.from, previousRange.to, token, filters),
    enabled: !!token,
  });
  const platformQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: ['gaBreakdown', apiEnvironment, 'platform', from, to, statisticsSession(token)],
    queryFn: () => getGaBreakdown('platform', from, to, token),
    enabled: !!token,
  });
  const versionQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: ['gaBreakdown', apiEnvironment, 'appVersion', from, to, statisticsSession(token)],
    queryFn: () => getGaBreakdown('appVersion', from, to, token),
    enabled: !!token,
  });
  const comparisonRows = useMemo(
    () => buildEventComparisonRows(currentQuery.data, previousQuery.data),
    [currentQuery.data, previousQuery.data],
  );
  const eventRows = useMemo(
    () =>
      prioritizeFavoriteEvents(
        filterAndSortEvents(comparisonRows, search, sortKey),
        favoriteEvents,
      ),
    [comparisonRows, favoriteEvents, search, sortKey],
  );
  const eventPlatformQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: [
      'gaBreakdown',
      apiEnvironment,
      'platform',
      from,
      to,
      { eventName: selectedEvent, appVersion },
      statisticsSession(token),
    ],
    queryFn: () =>
      getGaBreakdown('platform', from, to, token, { eventName: selectedEvent, appVersion }),
    enabled: !!token && !!selectedEvent,
  });
  const eventTrendQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: [
      'gaBreakdown',
      apiEnvironment,
      'date',
      from,
      to,
      { eventName: selectedEvent, platform, appVersion },
      statisticsSession(token),
    ],
    queryFn: () =>
      getGaBreakdown('date', from, to, token, {
        eventName: selectedEvent,
        platform,
        appVersion,
      }),
    enabled: !!token && !!selectedEvent,
  });
  const eventVersionQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: [
      'gaBreakdown',
      apiEnvironment,
      'appVersion',
      from,
      to,
      { eventName: selectedEvent, platform },
      statisticsSession(token),
    ],
    queryFn: () =>
      getGaBreakdown('appVersion', from, to, token, { eventName: selectedEvent, platform }),
    enabled: !!token && !!selectedEvent,
  });
  const chartData = useMemo(() => fillGaTimeseries(rows, from, to), [rows, from, to]);
  const eventTrendData = useMemo(
    () => fillGaTimeseries(toEventTrendRows(eventTrendQuery.data), from, to),
    [eventTrendQuery.data, from, to],
  );
  const hasEventQueryError = [currentQuery, previousQuery].some((query) => query.error);
  const hasListError = [currentQuery, previousQuery, platformQuery, versionQuery].some(
    (query) => query.error,
  );
  const hasBreakdownDetailError = [eventPlatformQuery, eventVersionQuery].some(
    (query) => query.error,
  );

  useEffect(() => {
    writeFavoriteEvents(typeof window === 'undefined' ? null : window.localStorage, favoriteEvents);
  }, [favoriteEvents]);

  return (
    <div className="space-y-4">
      {hasListError && (
        <p className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
          {t('dashboard.ga.loadError')}
        </p>
      )}
      <GaMetricLineChart
        title={t('analytics.events.trend')}
        data={chartData}
        isLoading={isLoading}
        lines={[
          { dataKey: 'eventCount', name: t('dashboard.ga.kpi.eventCount'), color: 'rgb(var(--admin-chart-orange))' },
          { dataKey: 'keyEvents', name: t('dashboard.ga.kpi.keyEvents'), color: 'rgb(var(--admin-chart-cyan))' },
        ]}
        heightClass="h-56 xl:h-64"
      />
      <EventAnomalySummary
        rows={comparisonRows}
        favoriteEvents={favoriteEvents}
        isLoading={currentQuery.isLoading || previousQuery.isLoading}
        hasError={hasEventQueryError}
      />
      <DashboardPanel title={t('analytics.events.tableTitle')}>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="flex min-w-[15rem] flex-1 flex-col gap-1 text-xs text-dark-text-muted">
            {t('analytics.events.search')}
            <span className="flex items-center gap-2 rounded-md border border-dark-border bg-dark-surface px-3 py-2">
              <Search className="h-4 w-4" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('analytics.events.searchPlaceholder')}
                className="min-w-0 flex-1 bg-transparent text-sm text-dark-text outline-none"
              />
            </span>
          </label>
          <Select
            label={t('analytics.events.platform')}
            value={platform}
            onChange={value => updateFilter('platform', value)}
            options={platformQuery.data?.map((item) => item.dimension)}
          />
          <Select
            label={t('analytics.events.appVersion')}
            value={appVersion}
            onChange={value => updateFilter('version', value)}
            options={versionQuery.data?.map((item) => item.dimension)}
          />
          <Select
            label={t('analytics.events.sort')}
            value={sortKey}
            onChange={setSortKey}
            options={['eventCount', 'changeRate']}
            includeAll={false}
            optionLabels={{
              eventCount: t('analytics.events.sortEventCount'),
              changeRate: t('analytics.events.sortChangeRate'),
            }}
          />
        </div>
        <p className="mb-3 text-xs text-dark-text-secondary">
          {t('analytics.events.comparison', {
            from: previousRange.from,
            to: previousRange.to,
          })}
        </p>
        {currentQuery.isLoading || previousQuery.isLoading ? (
          <p className="py-10 text-center text-dark-text-secondary">{t('common.loading')}</p>
        ) : hasEventQueryError ? null : eventRows.length === 0 ? (
          <p className="py-10 text-center text-dark-text-secondary">{t('dashboard.ga.noData')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] text-left text-sm">
              <thead className="border-b border-dark-border text-xs text-dark-text-secondary">
                <tr>
                  <th className="px-3 py-2">{t('analytics.events.event')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.events.current')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.events.previous')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.events.change')}</th>
                  <th className="px-3 py-2 text-right">{t('dashboard.ga.kpi.keyEvents')}</th>
                </tr>
              </thead>
              <tbody>
                {eventRows.slice(0, 100).map((row) => {
                  const isFavorite = favoriteEvents.includes(row.dimension);
                  const isSelected = selectedEvent === row.dimension;
                  return (
                  <tr
                    key={row.dimension}
                    aria-selected={isSelected}
                    className={`border-b border-dark-border/40 text-dark-text ${
                      isSelected ? 'bg-primary/10' : ''
                    }`}
                  >
                    <td className="px-3 py-2.5 font-medium">
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setFavoriteEvents((current) =>
                              toggleFavoriteEvent(current, row.dimension),
                            )
                          }
                          aria-label={t(
                            isFavorite
                              ? 'analytics.events.unfavorite'
                              : 'analytics.events.favorite',
                            { event: row.dimension },
                          )}
                          className={`rounded p-1 ${
                            isFavorite
                              ? 'text-amber-300'
                              : 'text-dark-text-muted hover:text-amber-300'
                          }`}
                        >
                          <Star className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedEvent(row.dimension)}
                          className="break-all text-left hover:text-primary hover:underline"
                        >
                          {row.dimension}
                        </button>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{row.eventCount.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{row.previousEventCount.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {row.changeRate == null ? t('analytics.events.new') : formatPercent(row.changeRate, 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{row.keyEvents.toLocaleString()}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DashboardPanel>
      {selectedEvent && (
        <section aria-label={t('analytics.events.detailTitle', { event: selectedEvent })}>
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h2 className="break-all text-lg font-semibold text-dark-text">
                {t('analytics.events.detailTitle', { event: selectedEvent })}
              </h2>
              <p className="mt-1 text-xs text-dark-text-secondary">
                {t('analytics.events.detailDescription')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedEvent('')}
              aria-label={t('analytics.events.closeDetail')}
              className="rounded-md border border-dark-border p-2 text-dark-text-secondary hover:border-primary hover:text-primary"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {eventTrendQuery.error ? (
            <p className="mb-4 rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
              {t('analytics.events.trendLoadError')}
            </p>
          ) : (
            <GaMetricLineChart
              title={t('analytics.events.selectedTrend')}
              data={eventTrendData}
              isLoading={eventTrendQuery.isLoading}
              lines={[
                {
                  dataKey: 'eventCount',
                  name: t('dashboard.ga.kpi.eventCount'),
                  color: 'rgb(var(--admin-chart-orange))',
                },
                {
                  dataKey: 'keyEvents',
                  name: t('dashboard.ga.kpi.keyEvents'),
                  color: 'rgb(var(--admin-chart-cyan))',
                },
              ]}
              heightClass="h-56 xl:h-64"
              className="mb-4"
            />
          )}
          {hasBreakdownDetailError && (
            <p className="mb-4 rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
              {t('analytics.events.detailLoadError')}
            </p>
          )}
          {!hasBreakdownDetailError && <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <GaBreakdownCard
              title={t('analytics.events.platformBreakdown')}
              items={eventPlatformQuery.data}
              isLoading={eventPlatformQuery.isLoading}
              metricKey="eventCount"
            />
            <GaBreakdownCard
              title={t('analytics.events.versionBreakdown')}
              items={eventVersionQuery.data}
              isLoading={eventVersionQuery.isLoading}
              metricKey="eventCount"
            />
          </div>}
        </section>
      )}
    </div>
  );
}

function CrashTab({ rows, isLoading, from, to }) {
  const latest = getLatestGaRow(rows);
  const chartData = useMemo(() => fillGaTimeseries(rows, from, to), [rows, from, to]);
  const tone = crashHealthTone(latest?.crashFreeUsersRate);
  const firebaseUrl = getFirebaseCrashlyticsUrl(
    process.env.REACT_APP_FIREBASE_CRASHLYTICS_PROD_URL,
  );
  const toneClass = {
    healthy: 'border-emerald-800 bg-emerald-950/30 text-emerald-300',
    warning: 'border-amber-800 bg-amber-950/30 text-amber-300',
    critical: 'border-red-800 bg-red-950/30 text-red-300',
    unknown: 'border-dark-border bg-dark-card text-dark-text-secondary',
  }[tone];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <DashboardKpi
          label={t('analytics.crash.crashFreeToday')}
          value={latest ? formatPercent(latest.crashFreeUsersRate) : '—'}
          accent
          size="primary"
        />
        <DashboardKpi
          label={t('analytics.crash.affectedToday')}
          value={latest?.crashAffectedUsers ?? '—'}
          size="primary"
        />
        <div className={`rounded-lg border p-4 ${toneClass}`}>
          <p className="text-xs uppercase tracking-wide">{t('analytics.crash.status')}</p>
          <p className="mt-2 text-xl font-semibold">{t(`analytics.crash.tone.${tone}`)}</p>
        </div>
      </div>
      <GaMetricLineChart
        title={t('analytics.crash.trend')}
        data={chartData}
        isLoading={isLoading}
        lines={[
          {
            dataKey: 'crashFreeUsersRate',
            name: t('analytics.crash.crashFreeUsers'),
            color: 'rgb(var(--admin-chart-green))',
          },
        ]}
        yAxisPercent
        heightClass="h-64 xl:h-72"
      />
      <div className="rounded-xl border border-dark-border bg-dark-card p-4 sm:flex sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold text-dark-text">{t('analytics.crash.firebaseTitle')}</h3>
          <p className="mt-1 text-sm text-dark-text-secondary">{t('analytics.crash.firebaseDescription')}</p>
        </div>
        <a
          href={firebaseUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-dark-bg sm:mt-0"
        >
          {t('analytics.crash.openFirebase')}
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

export default function Analytics() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const tab = location.pathname.split('/')[2] || 'overview';
  const [preset, setPreset] = useState(GA_PRESET_30D);
  const params = new URLSearchParams(location.search);
  const urlFrom = params.get('from'); const urlTo = params.get('to');
  const validUrlRange = /^\d{4}-\d{2}-\d{2}$/.test(urlFrom || '') && /^\d{4}-\d{2}-\d{2}$/.test(urlTo || '') && urlFrom <= urlTo;
  const [range, setRange] = useState(() => validUrlRange ? {from:urlFrom,to:urlTo} : getGaDateRange(GA_RANGE_30D));
  useEffect(() => { if (validUrlRange) setRange({from:urlFrom,to:urlTo}); }, [urlFrom,urlTo,validUrlRange]);
  const { from, to } = range;
  const dashboardQuery = useQuery({
    ...DAILY_STATISTICS_OPTIONS,
    queryKey: ['gaDashboard', apiEnvironment, from, to, statisticsSession(token)],
    queryFn: () => getGaDashboard(from, to, token),
    enabled: !!token && tab !== 'growth',
  });

  useEffect(() => {
    if (dashboardQuery.error?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [dashboardQuery.error, logout, navigate]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['gaDashboard', apiEnvironment] }),
      queryClient.invalidateQueries({ queryKey: ['gaBreakdown', apiEnvironment] }),
    ]);
  }, [apiEnvironment, queryClient]);

  if (!TAB_KEYS.includes(tab)) return <Navigate to="/analytics/overview" replace />;

  return (
    <div className="min-h-screen bg-dark-bg px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-none space-y-5">
        <div className="flex flex-col gap-4 border-b border-dark-border/60 pb-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-dark-text sm:text-3xl">{t('analytics.title')}</h1>
            <p className="mt-1 text-sm text-dark-text-secondary">{t('analytics.subtitle')}</p>
            {tab !== 'growth' && <p className="mt-1 text-xs text-dark-text-muted">
              {t('analytics.freshness', { date: getLatestGaRow(dashboardQuery.data)?.date || '—' })}
            </p>}
          </div>
          {tab !== 'growth' && <div className="flex flex-col items-start gap-3 xl:items-end">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={dashboardQuery.isFetching}
              className="inline-flex items-center gap-2 rounded-lg border border-dark-border px-3 py-2 text-sm font-medium text-dark-text hover:border-primary hover:text-primary disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${dashboardQuery.isFetching ? 'animate-spin' : ''}`} />
              {t('dashboard.refresh')}
            </button>
            <GaDateRangeControls
              preset={preset}
              appliedRange={range}
              onApply={({ preset: nextPreset, range: nextRange }) => {
                setPreset(nextPreset);
                setRange(nextRange);
                const nextParams = new URLSearchParams(location.search);
                nextParams.set('from', nextRange.from); nextParams.set('to', nextRange.to);
                navigate({pathname:location.pathname,search:`?${nextParams.toString()}`});
              }}
            />
          </div>}
        </div>

        <PageTabs label={t('analytics.tabs.label')} active={tab} items={TAB_KEYS.map(key => ({ id: key, to: `/analytics/${key}${location.search}`, label: t(`analytics.tabs.${key}`) }))} />

        {tab !== 'growth' && dashboardQuery.error && dashboardQuery.error.status !== 401 && (
          <p className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
            {t('dashboard.ga.loadError')}
          </p>
        )}

        {tab === 'growth' && <GrowthTab />}

        {tab === 'overview' && (
          <OverviewTab
            rows={dashboardQuery.data}
            isLoading={dashboardQuery.isLoading}
            from={from}
            to={to}
            token={token}
            apiEnvironment={apiEnvironment}
          />
        )}
        {tab === 'events' && (
          <EventsTab
            rows={dashboardQuery.data}
            isLoading={dashboardQuery.isLoading}
            from={from}
            to={to}
            token={token}
            apiEnvironment={apiEnvironment}
          />
        )}
        {tab === 'crash' && (
          <CrashTab
            rows={dashboardQuery.data}
            isLoading={dashboardQuery.isLoading}
            from={from}
            to={to}
          />
        )}
      </div>
    </div>
  );
}
